import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * Colour scheme.
 *
 * Three states, not two. "System" is the default because an admin who has set
 * their OS to dark at 9pm has already told us what they want, and a hard-coded
 * light panel at that hour is the complaint we would get. An explicit choice
 * always wins over the OS.
 *
 * The resolved value is written to `<html data-theme>`, which is what the
 * Tailwind preset keys its dark tokens on.
 */

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'kinshipos.theme';

interface ThemeState {
  preference: ThemePreference;
  /** What is actually on screen — `system` resolved against the OS. */
  resolved: ResolvedTheme;
  setPreference: (preference: ThemePreference) => void;
  /** Cycles light → dark → system, for the header's single button. */
  cycle: () => void;
  /** Re-reads the OS setting. Called when the media query changes. */
  syncWithSystem: () => void;
}

export function systemTheme(): ResolvedTheme {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function resolveTheme(preference: ThemePreference): ResolvedTheme {
  return preference === 'system' ? systemTheme() : preference;
}

/**
 * Writes the theme to the document.
 *
 * `color-scheme` matters as much as the attribute: without it the browser
 * paints form controls, scrollbars and the space past the page in light
 * chrome, and a dark page ends up with a white scrollbar down its edge.
 */
export function applyTheme(resolved: ResolvedTheme): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.setAttribute('data-theme', resolved);
  root.style.colorScheme = resolved;
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      preference: 'system',
      resolved: 'light',

      setPreference: (preference) => {
        const resolved = resolveTheme(preference);
        applyTheme(resolved);
        set({ preference, resolved });
      },

      cycle: () => {
        const order: ThemePreference[] = ['light', 'dark', 'system'];
        const next = order[(order.indexOf(get().preference) + 1) % order.length] ?? 'system';
        get().setPreference(next);
      },

      syncWithSystem: () => {
        if (get().preference !== 'system') return;
        const resolved = systemTheme();
        applyTheme(resolved);
        set({ resolved });
      },
    }),
    {
      name: THEME_STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      // Only the choice is persisted. `resolved` is derived, and storing it
      // would show yesterday's theme for a frame after the OS changed.
      partialize: (state) => ({ preference: state.preference }),
      onRehydrateStorage: () => (state) => {
        if (state) state.setPreference(state.preference);
      },
    },
  ),
);

/**
 * Applies the stored theme as early as possible, before React mounts.
 *
 * Inlined into `index.html` rather than imported, because a module that has to
 * be fetched and parsed still lets one light frame through — the flash people
 * describe as "it blinks white when I open it".
 */
export const THEME_BOOTSTRAP_SCRIPT = `
(function () {
  try {
    var stored = localStorage.getItem('${THEME_STORAGE_KEY}');
    var preference = stored ? (JSON.parse(stored).state || {}).preference : 'system';
    var dark = preference === 'dark' ||
      (preference !== 'light' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    var theme = dark ? 'dark' : 'light';
    document.documentElement.setAttribute('data-theme', theme);
    document.documentElement.style.colorScheme = theme;
  } catch (e) {
    /* Private mode blocks localStorage; light is a safe floor. */
  }
})();
`.trim();
